import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { CustomField, ContextApi } from 'react-uforms';
import OutsideClickHandler from 'react-outside-click-handler';
import { DateTime } from 'luxon';
import { WithStyle } from '../../../utils';
import { IconCalendar, IconClose } from '../../icon';
import { InputGroup, InputGroupBlock, InputGroupAddon } from '../../input-group';
import { FormDatepickerStyled, FormDatepickerIconStyled, FormDatepickerIconCloseStyled } from './date.styled';
import { FormDateProps } from './types';
import { FormRadio } from '@ch/common/components/form/radio';
import { Box } from '@ch/common/components/box';
import { Text } from '@ch/common/components/text';

export enum FormatOptions {
  FULL_DATE = 'dd-MMM-yyyy',
  MONTH_YEAR = 'MMM-yyyy',
  YEAR = 'yyyy',
}

export const FormDate: FC<FormDateProps> & WithStyle = props => {
  const { setValue, getValue } = useContext(ContextApi);
  const {
    id,
    name,
    isPartialDate = false,
    dateFormat = FormatOptions.FULL_DATE,
    outputDateFormat = FormatOptions.FULL_DATE,
    placeholderText = 'Enter',
    locale = 'en',
    minDate = '1-Jun-1920',
    updateAnalyticsData,
  } = props;
  const [isCalendarOpen, setCalendarOpenState] = useState(false);
  const formatOption = `${name}_FormatOption`;

  useEffect(() => {
    setValue(formatOption, FormatOptions.FULL_DATE);
  }, [name]);

  return (
    <>
      <div>{props.$error}</div>
      <InputGroup>
        <InputGroupBlock>
          <FormDatepickerStyled>
            <CustomField {...{ name, hideError: true, validateOnChange: true }}>
              {({ setTouched }) => {
                const handleChange = useCallback(
                  (value = null) => {
                    setCalendarOpenState(false);
                    const date = value
                      ? DateTime.fromJSDate(value).toFormat(getValue(formatOption) || outputDateFormat)
                      : null;
                    setValue(name, date);
                    setTouched();
                  },
                  [setCalendarOpenState, setTouched, setValue, getValue],
                );
                return (
                  <OutsideClickHandler
                    onOutsideClick={() => {
                      setCalendarOpenState(false);
                    }}
                  >
                    <DatePicker
                      {...{ dateFormat, placeholderText, name, ...props }}
                      onFocus={() => {
                        setCalendarOpenState(true);
                        setTouched();
                      }}
                      value={getValue(name)}
                      onSelect={handleChange}
                      readOnly
                      onChange={() => {
                        return;
                      }}
                      locale={locale}
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={102}
                      open={isCalendarOpen}
                      minDate={new Date(minDate)}
                      // showYearPicker={api.getValue(formatOption) === FormatOptions.YEAR}
                      // showMonthYearPicker={api.getValue(formatOption) === FormatOptions.MONTH_YEAR}
                    />
                    {getValue(name) && (
                      <FormDatepickerIconCloseStyled onClick={() => handleChange()}>
                        <IconClose $size={10} />
                      </FormDatepickerIconCloseStyled>
                    )}
                  </OutsideClickHandler>
                );
              }}
            </CustomField>
          </FormDatepickerStyled>
        </InputGroupBlock>
        <InputGroupAddon
          onClick={() => {
            setCalendarOpenState(true);
          }}
        >
          <FormDatepickerIconStyled htmlFor={id}>
            <IconCalendar $size={18} $color="primary" />
          </FormDatepickerIconStyled>
        </InputGroupAddon>
      </InputGroup>
      {isPartialDate && (
        <Box $display="flex" $justifyContent="left" $m={[0, 0, 0, 2]}>
          <FormRadio name={formatOption} $size="md" value={FormatOptions.FULL_DATE} onChange={updateAnalyticsData}>
            <Text $m={[5, 15, 5, 0]} $size="md">
              DD-MMM-YY
            </Text>
          </FormRadio>
          <FormRadio name={formatOption} $size="md" value={FormatOptions.MONTH_YEAR} onChange={updateAnalyticsData}>
            <Text $m={[5, 15, 5, 0]} $size="md">
              MMM-YYYY
            </Text>
          </FormRadio>
          <FormRadio name={formatOption} $size="md" value={FormatOptions.YEAR} onChange={updateAnalyticsData}>
            <Text $m={[5, 15, 5, 0]} $size="md">
              YYYY
            </Text>
          </FormRadio>
        </Box>
      )}
    </>
  );
};
